import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PlusIcon } from '@navikt/aksel-icons';
import classes from './SurveyDistributionChannels.module.css';
import { useGetDistributionChannelsQuery } from '../../../hooks';
import { SurveyFormData } from '../SurveyFormCard';
import { GhostLoader } from '../../GhostLoader';
import { DistributionChannel } from '../../../api/queries';
import { useDeleteDistributionChannelMutation } from '../../../hooks/mutations/useDeleteDistributionChannelMutation';
import { Dialog } from '../../Dialog';
import { DeleteDistributionChannelDialog } from '../../DeleteDistributionChannelDialog/DeleteDistributionChannelDialog';
import { AddDistributionChannelCompact } from './AddDistributionChannelCompact/AddDistributionChannelCompact';
import { DistributionChannelListItem } from './DistributionChannelListItem/DistributionChannelListItem';

const TOAST_POSITION = 'bottom-right' as const;

type SurveyDistributionChannelsProps = {
  formData: SurveyFormData;
};

export const SurveyDistributionChannels = ({
  formData,
}: SurveyDistributionChannelsProps) => {
  const { t } = useTranslation();
  const [channelToDelete, setChannelToDelete] =
    useState<DistributionChannel | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const teamId = formData.team;
  const {
    data: distributionChannels,
    isLoading,
    error,
  } = useGetDistributionChannelsQuery(teamId);
  const { mutate: deleteDistributionChannel } =
    useDeleteDistributionChannelMutation(teamId);

  const onDeleteDistributionChannel = (channel: DistributionChannel) => {
    deleteDistributionChannel(channel.id, {
      onSuccess: () => {
        toast.success(t('surveyCreation.distributionChannels.delete.success'), {
          position: TOAST_POSITION,
        });
        setChannelToDelete(null);
      },
      onError: () => {
        toast.error(t('surveyCreation.distributionChannels.delete.error'), {
          position: TOAST_POSITION,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className={classes.container}>
        <GhostLoader style={{ height: 72, width: '100%' }} />
        <GhostLoader style={{ height: 72, width: '100%' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds-alert" data-color="danger" role="alert">
        <h2 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
          {t('surveyCreation.distributionChannels.error')}
        </h2>
        <p className="ds-paragraph">{error.message}</p>
      </div>
    );
  }

  if (!distributionChannels) {
    return (
      <p className="ds-paragraph">
        {t('surveyCreation.distributionChannels.notFound')}
      </p>
    );
  }

  const hasChannels = distributionChannels.length > 0;
  const showPicker = !hasChannels || isAdding;

  return (
    <div className={classes.container}>
      {hasChannels ? (
        <section className={classes.section}>
          <h2 className={`ds-heading ${classes.sectionHeading}`} data-size="2xs">
            {t('surveyCreation.distributionChannels.selectedTitle')}
          </h2>
          <ul className={classes.channelList}>
            {distributionChannels.map((channel) => (
              <DistributionChannelListItem
                key={channel.id}
                channel={channel}
                onRemove={setChannelToDelete}
              />
            ))}
          </ul>
          {!isAdding && (
            <div>
              <button
                type="button"
                className="ds-button"
                data-variant="secondary"
                data-size="sm"
                onClick={() => setIsAdding(true)}
              >
                <PlusIcon aria-hidden="true" fontSize="1.125rem" />
                {t('surveyCreation.distributionChannels.addNew')}
              </button>
            </div>
          )}
        </section>
      ) : (
        <div className="ds-alert" data-color="info">
          <h2 className={`ds-heading ${classes.alertHeading}`} data-size="2xs">
            {t('surveyCreation.distributionChannels.noChannels.title')}
          </h2>
          <p className="ds-paragraph">
            {t('surveyCreation.distributionChannels.noChannels.description')}
          </p>
        </div>
      )}

      {showPicker && (
        <section className={classes.section}>
          <div className={classes.pickerHeader}>
            <h2 className={`ds-heading ${classes.sectionHeading}`} data-size="2xs">
              {t('surveyCreation.distributionChannels.addTitle')}
            </h2>
            {isAdding && (
              <button
                type="button"
                className="ds-button"
                data-variant="tertiary"
                data-size="sm"
                onClick={() => setIsAdding(false)}
              >
                {t('surveyCreation.distributionChannels.close')}
              </button>
            )}
          </div>
          <AddDistributionChannelCompact
            teamId={teamId}
            existingChannels={distributionChannels}
            onChannelAdded={() => setIsAdding(false)}
          />
        </section>
      )}

      <Dialog
        isOpen={channelToDelete !== null}
        closeDialog={() => setChannelToDelete(null)}
      >
        <DeleteDistributionChannelDialog
          channelToDelete={channelToDelete}
          setChannelToDelete={setChannelToDelete}
          onDeleteDistributionChannel={onDeleteDistributionChannel}
        />
      </Dialog>
    </div>
  );
};
